import { Sparkles, Heart } from 'lucide-react';
import type { DayStats } from '../../lib/weekStats';
import { dateKey } from '../../lib/setProgress';

interface WeekHeatmapProps {
  stats: readonly DayStats[];
  todayKey: string;
}

const DOW_SHORT: Record<DayStats['dayOfWeek'], string> = {
  mon: 'Mo',
  tue: 'Di',
  wed: 'Mi',
  thu: 'Do',
  fri: 'Fr',
  sat: 'Sa',
  sun: 'So',
};

export function WeekHeatmap({ stats, todayKey }: WeekHeatmapProps): JSX.Element {
  return (
    <ol className="grid grid-cols-7 gap-1.5" aria-label="Wochenuebersicht">
      {stats.map((s) => {
        const isToday = dateKey(s.date) === todayKey;
        return (
          <li key={s.dayKey} className="flex flex-col items-center gap-1">
            <span className="text-[11px] font-semibold text-zinc-600">
              {DOW_SHORT[s.dayOfWeek]}
            </span>
            <HeatmapCell stats={s} isToday={isToday} />
          </li>
        );
      })}
    </ol>
  );
}

function HeatmapCell({ stats, isToday }: { stats: DayStats; isToday: boolean }): JSX.Element {
  const borderClass = isToday ? 'ring-2 ring-emerald-500 ring-offset-1' : '';
  const ariaLabel = labelFor(stats);

  if (stats.plan.kind === 'pilates') {
    return (
      <span
        aria-label={ariaLabel}
        className={`flex aspect-square w-full items-center justify-center rounded-lg border border-amber-300 bg-amber-50 text-amber-600 ${borderClass}`}
      >
        <Sparkles aria-hidden="true" className="size-4" />
      </span>
    );
  }
  if (stats.plan.kind === 'recovery') {
    return (
      <span
        aria-label={ariaLabel}
        className={`flex aspect-square w-full items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-600 ${borderClass}`}
      >
        <Heart aria-hidden="true" className="size-4" />
      </span>
    );
  }

  // training day — Fill-Height entspricht abgeschlossenen Uebungen
  const ratio = stats.totalExercises > 0 ? stats.completedExercises / stats.totalExercises : 0;
  const isFull = stats.completedExercises === stats.totalExercises && stats.totalExercises > 0;
  const fillPct = Math.round(ratio * 100);

  return (
    <span
      aria-label={ariaLabel}
      className={`relative flex aspect-square w-full items-end overflow-hidden rounded-lg border border-zinc-200 bg-zinc-100 ${borderClass}`}
    >
      <span
        aria-hidden="true"
        className={`block w-full ${isFull ? 'bg-emerald-500' : 'bg-emerald-400'}`}
        style={{ height: `${fillPct}%` }}
      />
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold tabular-nums text-zinc-700 mix-blend-difference">
        {stats.completedExercises}/{stats.totalExercises}
      </span>
    </span>
  );
}

function labelFor(s: DayStats): string {
  if (s.plan.kind === 'pilates') return `${DOW_SHORT[s.dayOfWeek]}: Pilates`;
  if (s.plan.kind === 'recovery') return `${DOW_SHORT[s.dayOfWeek]}: Recovery`;
  return `${DOW_SHORT[s.dayOfWeek]}: ${s.completedExercises} von ${s.totalExercises} Uebungen`;
}
